import React, { useState, useCallback, useEffect, useRef } from 'react'
import debounce from 'lodash/debounce'
import { FilterSettings, FilterTypes } from '../../types/Main'
import decodeFilters from '../utils/decodeFilters'
import applyFilters from '../utils/applyFilters'
import InputSearch from '../utils/InputSearch'
import hasFilterApplied from '../utils/hasFilterApplied'
import Tags from './Tags'
import Properties from './Properties'
import PriceFilter from '../PriceFilter'
import Sort from './Sort'
import FilteredItems from '../utils/filteredItems/filteredItems'
import './VerticalMode.scss'
import useI18n from 'src/hooks/useI18n'

const { initialTags, initialProperties, initialSort, initialPrice } = decodeFilters()

const VerticalMode = (props: FilterSettings): React.ReactElement => {
  const {
    mode,
    showAppliedFilters,
    filtersStartOpen,
    filterOnClick,
    resetMode,
    tags,
    properties,
    hasSort,
    sort,
    price,
    priceProps,
    hasInputSearch
  } = props

  const BASE_CLASS_CSS = 'filter-vertical'

  const [filteredTags, setFilteredTags] = useState(initialTags)
  const [filteredProperties, setFilteredProperties] = useState(initialProperties)
  const [filteredSort, setFilteredSort] = useState(initialSort)
  const [filteredMinPrice, setFilteredMinPrice] = useState(initialPrice[0])
  const [filteredMaxPrice, setFilteredMaxPrice] = useState(initialPrice[1])

  const prevFiltersRef = useRef({
    tags: initialTags,
    properties: initialProperties,
    sort: initialSort,
    minPrice: initialPrice[0],
    maxPrice: initialPrice[1]
  })

  const debouncedApplyFilters = useCallback(debounce((filters) => {
    applyFilters(filters)
  }, 1000), [])

  useEffect(() => {
    const customLoadEvent = new CustomEvent('vnda:filter-component-loaded', {
      detail: {
        filterClass: BASE_CLASS_CSS
      }
    })
    window.dispatchEvent(customLoadEvent)
  }, [])

  useEffect(() => {
    if (!filterOnClick) return

    const currentFilters = {
      tags: filteredTags,
      properties: filteredProperties,
      sort: filteredSort,
      minPrice: filteredMinPrice,
      maxPrice: filteredMaxPrice
    }

    // Verificar se houve alguma mudança nos filtros
    const hasFiltersChanged = (Object.keys(prevFiltersRef.current) as (keyof typeof prevFiltersRef.current)[]).some(
      (key) => currentFilters[key] !== prevFiltersRef.current[key]
    )

    // Se houver alguma mudança nos filtros, aplica os filtros e atualiza a referência
    if (hasFiltersChanged) {
      debouncedApplyFilters(currentFilters)
      prevFiltersRef.current = currentFilters
    }
  }, [filteredMinPrice, filteredMaxPrice, filteredTags, filteredProperties, filteredSort, filterOnClick])

  const clearFilter = useCallback((
    callback: Function,
    name: FilterTypes
  ) => (value: any) => {
    const currentFilters = {
      tags: filteredTags,
      properties: filteredProperties,
      sort: filteredSort,
      minPrice: filteredMinPrice,
      maxPrice: filteredMaxPrice
    }
    callback(value)
    applyFilters({
      ...currentFilters,
      [name]: ''
    })
  }, [filteredTags, filteredProperties, filteredSort, filteredMinPrice, filteredMaxPrice])

  const clearPriceFilter = useCallback(() => {
    const currentFilters = {
      tags: filteredTags,
      properties: filteredProperties,
      sort: filteredSort
    }
    if (price) {
      setFilteredMinPrice(price[0])
      setFilteredMaxPrice(price[1])
    }
    applyFilters({ ...currentFilters, minPrice: -1, maxPrice: -1 })
  }, [filteredTags, filteredProperties, filteredSort, price])

  const clearAllFilters = () => {
    applyFilters({ tags: {}, properties: {}, sort: {}, minPrice: -1, maxPrice: -1 })
  }

  const handlesApply = () => {
    applyFilters({
      tags: filteredTags,
      properties: filteredProperties,
      sort: filteredSort,
      minPrice: filteredMinPrice,
      maxPrice: filteredMaxPrice
    })
  }

  const translation = useI18n()

  return (
    <>
      <div
        className={`${BASE_CLASS_CSS}`}>
          <span className={`${BASE_CLASS_CSS}-title`}>Filtros</span>
        {showAppliedFilters && (
          <FilteredItems
            appliedTags={prevFiltersRef.current.tags}
            appliedProperties={prevFiltersRef.current.properties}
            appliedSort={prevFiltersRef.current.sort}
            appliedMinPrice={prevFiltersRef.current.minPrice}
            appliedMaxPrice={prevFiltersRef.current.maxPrice}
            properties={properties}
            tags={tags}
            minPrice={price ? price[0] : undefined}
            maxPrice={price ? price[1] : undefined}
          />
        )}
        {hasInputSearch && <InputSearch />}
        <Tags
          tags={tags}
          startOpen={filtersStartOpen}
          resetMode={resetMode}
          filteredTags={filteredTags}
          filterOnClick={filterOnClick}
          setFilteredTags={setFilteredTags}
          clearTags={clearFilter(setFilteredTags, 'tags')}
        />
        <Properties
          properties={properties}
          startOpen={filtersStartOpen}
          resetMode={resetMode}
          filteredProperties={filteredProperties}
          filterOnClick={filterOnClick}
          setFilteredProperties={setFilteredProperties}
          clearProperties={clearFilter(setFilteredProperties, 'properties')}
        />
        {price && (
          <PriceFilter
            mode={mode}
            startOpen={filtersStartOpen}
            minPrice={price[0]}
            maxPrice={price[1]}
            priceProps={priceProps}
            resetMode={resetMode}
            filteredMinPrice={filteredMinPrice}
            filteredMaxPrice={filteredMaxPrice}
            filterOnClick={filterOnClick}
            setFilteredMinPrice={setFilteredMinPrice}
            setFilteredMaxPrice={setFilteredMaxPrice}
            clearPriceFilter={clearPriceFilter}
          />
        )}
        {hasSort && (
          <Sort
            sort={sort}
            startOpen={filtersStartOpen}
            resetMode={resetMode}
            filteredSort={filteredSort}
            filterOnClick={filterOnClick}
            setFilteredSort={setFilteredSort}
            clearSort={clearFilter(setFilteredSort, 'sort')}
          />
        )}
      </div>
      <div
        className={`${BASE_CLASS_CSS}-buttons filter-buttons`}
      >
        {!filterOnClick && (
          <button
            className={`${BASE_CLASS_CSS}-button-apply filter-button-apply`}
            onClick={handlesApply}
          >
            {translation.filter.apply}
          </button>
        )}
        {resetMode === 'all' && hasFilterApplied && (
          <button
            className={`${BASE_CLASS_CSS}-button-clear filter-button-clear`}
            onClick={clearAllFilters}
          >
            {translation.filter.cleanFilters}
          </button>
        )}
      </div>
    </>
  )
}

export default VerticalMode
